import { ApolloError, DocumentNode } from '@apollo/client';
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverTrigger,
  Tooltip,
  useClipboard,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import ChatSharpIcon from '@gamma/ui/icons/ChatSharpIcon';
import CheckCircleSharpIcon from '@gamma/ui/icons/CheckCircleSharpIcon';
import ContentCopySharpIcon from '@gamma/ui/icons/ContentCopySharpIcon';
import ForwardSharpIcon from '@gamma/ui/icons/ForwardSharpIcon';
import KeyboardArrowDownSharpIcon from '@gamma/ui/icons/KeyboardArrowDownSharpIcon';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useContext, useEffect, useState, useMemo } from 'react';
import { IGetDetectionVariables } from '../..';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  SlideDrawerContext,
} from '@investigator/shared/providers';
import { DetectionQueryVariables } from '../../../pages';
import { IQueryAlertMetadataExcludedEntitiesPaginated } from '../../../queries';
import {
  AssignUserContent,
  CloseDetectionContent,
  SendToContent,
  SuppressEntityContent,
} from '../PopoverContent';
import { AddNotePopover } from '../PopoverContent/AddNote/AddNotePopover';
import { useDetectionContext } from '@investigator/features/detections/context';
import { SuppressEntityMenuItem } from '@investigator/shared/components/SuppressEntityMenuItem';
import type { SantizedDetection } from '../../../pages/Detections';
import {
  getSourceEntityForSuppression,
  getEntityInstanceFromDetection,
} from '@investigator/shared/utils';
import { useEntitiesContext } from '@investigator/features/entities/context/EntitiesContext';
import { useEntitySuppressionStatus } from '@investigator/features/entities/hooks';
import { Detection } from '@investigator/shared/generated/graphql';

const { excludeEntity } = i18n.pages.entityDetails;

const { detectionIsClosed, suppress, unSuppress } = excludeEntity;

const {
  close,
  sendTo,
  controls,
  copiedUrl,
  detectionIsSent,
  copyDetectionUrl,
  SNConfigureAndEnable,
  detection: { addNote },
} = i18n.pages.detections;

const { actions, assignTo } = controls;

interface ActionsMenuProps {
  detection?: Detection | SantizedDetection;
  isRelated?: boolean;
  refetchQueries: (
    | { query: DocumentNode; variables: IGetDetectionVariables }
    | { query: DocumentNode; variables: DetectionQueryVariables }
  )[];
  isRowActions?: boolean;
  isRowActionsExpanded?: boolean;
  excludedEntitiesData?: IQueryAlertMetadataExcludedEntitiesPaginated;
  excludedEntitiesError?: ApolloError;
  excludedEntitiesLoading: boolean;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  userRole?: string;
}

export const ActionsMenuButton = ({
  detection,
  refetchQueries,
  excludedEntitiesData,
  excludedEntitiesLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
  userRole,
}: ActionsMenuProps) => {
  const toast = useToast();
  const { detectionData: contextDetectionData } = useDetectionContext();
  const { setSelectedEntity, suppression, selectedEntity } =
    useEntitiesContext();

  // Use the prop detection if provided (for row actions), otherwise fall back to context
  const detectionData = detection ?? contextDetectionData;

  const sourceEntity = useMemo(
    () => getSourceEntityForSuppression(detectionData),
    [detectionData],
  );

  const { isNotesDrawerLoading } = useContext(SlideDrawerContext);
  const { userPrivileges, userPrivilegesLoading, userLicense } =
    useContext(AuthContext);
  const { isOpen, onToggle, onClose: closePopover } = useDisclosure();
  const [popoverType, setPopoverType] = useState<null | string>(null);

  const [closeLoading, setCloseLoading] = useState<boolean>(false);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [sendToLoading, setSendToLoading] = useState<boolean>(false);

  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [isSentTo, setIsSentTo] = useState<boolean>(false);

  const { hasCopied, onCopy } = useClipboard(window.location.href);

  const onClose = () => {
    setPopoverType(null);
    closePopover();
  };
  useEffect(() => {
    if (hasCopied) {
      toast({
        position: 'top',
        status: 'success',
        description: copiedUrl,
      });
    }
  }, [hasCopied, toast]);

  const tenant = userLicense?.tenant;
  const serviceNowEnabled = tenant
    ? userPrivileges?.[tenant]?.['service-now']
    : false;
  const escalationConfigurationsLoading = userPrivilegesLoading;

  // Use shared hook to determine suppression status
  const isSuppressed = useEntitySuppressionStatus({
    selectedEntity,
    sourceEntity,
    excludedEntitiesData,
  });

  useEffect(() => {
    setIsClosed(detectionData?.detection_status !== 'open');
    setIsSentTo(!!detectionData?.escalation_data?.escalation_status);
  }, [detectionData]);

  const menuDisabled =
    closeLoading ||
    excludedEntitiesLoading ||
    suppression.pendingUpdate ||
    assignLoading ||
    sendToLoading ||
    isNotesDrawerLoading ||
    escalationConfigurationsLoading;

  return userRole === 'viewer' ? (
    <Button
      onClick={onCopy}
      variant="solid"
      colorScheme="gray"
      data-testid="detection-details-header-copy-button"
    >
      <ContentCopySharpIcon boxSize={5} />
      {copyDetectionUrl}
    </Button>
  ) : (
    <VStack>
      <Popover
        isOpen={isOpen}
        offset={[0, 10]}
        onClose={onClose}
        placement="bottom-start"
      >
        {' '}
        <Menu>
          <PopoverTrigger>
            <MenuButton
              as={Button}
              variant="solid"
              disabled={menuDisabled}
              colorScheme="gray"
              data-testid="actions-menu-toggle-button"
              rightIcon={
                menuDisabled ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <KeyboardArrowDownSharpIcon />
                )
              }
            >
              {actions}
            </MenuButton>
          </PopoverTrigger>
          <MenuList zIndex="popover">
            <VStack spacing={0} alignItems="start">
              <MenuItem
                onClick={onCopy}
                icon={<ContentCopySharpIcon boxSize={5} />}
              >
                {copyDetectionUrl}
              </MenuItem>
              <Tooltip
                offset={[-9, 6]}
                placement="left-start"
                shouldWrapChildren={true}
                label={isClosed ? detectionIsClosed : null}
              >
                <MenuItem
                  isDisabled={isClosed}
                  data-testid="close-detection-trigger-actions-menu-button"
                  onClick={() => {
                    onToggle();
                    setPopoverType('close');
                  }}
                  icon={<CheckCircleSharpIcon boxSize={5} />}
                >
                  {close}
                </MenuItem>
              </Tooltip>
              <MenuItem
                data-testid="add-note-detection-trigger-actions-menu-button"
                onClick={() => {
                  onToggle();
                  setPopoverType('addNote');
                }}
                icon={<ChatSharpIcon />}
              >
                {addNote}
              </MenuItem>
              <Tooltip
                offset={[-9, 6]}
                placement="left-start"
                shouldWrapChildren={true}
                label={
                  isSentTo
                    ? detectionIsSent
                    : !sendToLoading &&
                        !escalationConfigurationsLoading &&
                        !serviceNowEnabled
                      ? SNConfigureAndEnable
                      : null
                }
              >
                <MenuItem
                  isDisabled={isSentTo || !serviceNowEnabled}
                  data-testid="send-to-trigger-actions-menu-button"
                  onClick={() => {
                    onToggle();
                    setPopoverType('sendTo');
                  }}
                  icon={<ForwardSharpIcon boxSize={5} />}
                >
                  {sendTo}
                </MenuItem>
              </Tooltip>
              <SuppressEntityMenuItem
                isSuppressed={isSuppressed}
                isDisabled={!sourceEntity}
                onClick={() => {
                  // Map detection data to EntityInstance and set in context
                  if (sourceEntity && detectionData) {
                    const entityInstance = {
                      ...getEntityInstanceFromDetection(
                        detectionData,
                        sourceEntity,
                      ),
                      entityIsSuppressed: isSuppressed,
                    };
                    setSelectedEntity(entityInstance);
                  }
                  onToggle();
                  setPopoverType('suppress');
                }}
                testId="suppress-entity-trigger-actions-menu-button"
                suppressText={suppress}
                unsuppressText={unSuppress}
              />
              <MenuItem
                data-testid="assign-detection-trigger-actions-menu-button"
                onClick={() => {
                  onToggle();
                  setPopoverType('assign');
                }}
                icon={<AccountCircleSharpIcon boxSize={5} />}
              >
                {assignTo}
              </MenuItem>
            </VStack>
          </MenuList>
        </Menu>
        {popoverType === 'close' && (
          <CloseDetectionContent
            onClose={onClose}
            setIsClosed={setIsClosed}
            setIsLoading={setCloseLoading}
            refetchQueries={refetchQueries}
            refetchMain={true}
            excludedEntitiesLoading={excludedEntitiesLoading}
            isLoading={closeLoading}
          />
        )}
        {popoverType === 'sendTo' && (
          <SendToContent
            onClose={onClose}
            setIsSentTo={setIsSentTo}
            setIsLoading={setSendToLoading}
            refetchQueries={refetchQueries}
            refetchMain={true}
            excludedEntitiesLoading={excludedEntitiesLoading}
            isLoading={sendToLoading}
          />
        )}
        {popoverType === 'suppress' && (
          <SuppressEntityContent
            onClose={onClose}
            refetchMain={true}
            excludedEntitiesLoading={excludedEntitiesLoading}
            isSuppressed={isSuppressed}
          />
        )}
        {popoverType === 'assign' && (
          <AssignUserContent
            detection={detection} // Pass detection prop for row actions, undefined for drawer
            onClose={onClose}
            setIsLoading={setAssignLoading}
            isLoading={assignLoading}
            refetchMain={true}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
          />
        )}
        {popoverType === 'addNote' && (
          <AddNotePopover onClose={onClose} refetchMain={true} />
        )}
      </Popover>
    </VStack>
  );
};
